(() => {
  if (window.__SQ_EXPANDED_CARD_FINAL_POLISH__) return;
  window.__SQ_EXPANDED_CARD_FINAL_POLISH__ = true;

  const MODAL = '[data-lcs-favorite-expanded]';
  const CLOSE = '[data-lcs-expanded-close],.lcs-favorite-expanded__close--media,.lcs-favorite-expanded__close';
  const SIDE = '.lcs-favorite-expanded__side';
  const THUMB = '.lcs-favorite-expanded__thumb';
  const OPENERS = [
    '[data-lcs-universal-choose]',
    '[data-pick-quick-open]',
    '[data-sq-universal-expanded-open]',
    '[data-lcs-favorite-expand]',
    '[data-sqyml2-expand]',
    '[data-lcs-recommend-expand]',
    '[data-sqfd-open]'
  ].join(',');
  const CARD = '[data-sq-universal-card],[data-sq-rebuilt-card],[data-lcs-recommend-card],[data-sqyml2-card],[data-product-slider-card],[data-product-card],.product-slider-belt-card,product-card';

  const productCache = new Map();
  const imageCache = new Set();
  let suppressCloseClickUntil = 0;
  let frameId = 0;

  const clean = (value) => String(value ?? '').trim();
  const rootPath = () => String(window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  const pages = () => Array.from(document.querySelectorAll('[data-lcs-favorites-page]'));

  const normalizeImage = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    return clean(value.src || value.url || value.image || value.preview_image?.src || value.preview_image);
  };

  const preloadImage = (src, high = false) => {
    src = clean(src);
    if (!src || imageCache.has(src)) return;
    imageCache.add(src);
    const image = new Image();
    image.decoding = 'async';
    try { image.fetchPriority = high ? 'high' : 'auto'; } catch (_) {}
    image.src = src;
  };

  const handleFor = (opener) => {
    const card = opener?.closest(CARD) || opener?.closest('quick-add-component')?.parentElement;
    const quick = opener?.closest('quick-add-component');
    const direct = clean(quick?.dataset.productHandle || card?.dataset.productHandle || card?.dataset.handle);
    if (direct) return direct;
    const href = card?.querySelector('a[href*="/products/"]')?.getAttribute('href') || '';
    return clean(href.split('/products/')[1]?.split(/[?#/]/)[0]);
  };

  const prefetchProduct = (opener) => {
    const handle = handleFor(opener);
    if (!handle) return Promise.resolve(null);
    if (productCache.has(handle)) return productCache.get(handle);

    const request = fetch(`${rootPath()}products/${encodeURIComponent(handle)}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then((response) => response.ok ? response.json() : null)
      .then((product) => {
        if (!product) return null;
        const sources = [];
        normalizeImage(product.featured_image) && sources.push(normalizeImage(product.featured_image));
        (product.variants || []).forEach((variant) => {
          const src = normalizeImage(variant?.featured_image);
          if (src) sources.push(src);
        });
        (product.media || product.images || []).forEach((media) => {
          const src = normalizeImage(media);
          if (src) sources.push(src);
        });
        Array.from(new Set(sources)).slice(0, 12).forEach((src, index) => preloadImage(src, index < 5));
        return product;
      })
      .catch(() => null);

    productCache.set(handle, request);
    return request;
  };

  const eagerLoadSide = (modal) => {
    if (!(modal instanceof HTMLElement) || modal.hidden) return;
    const images = Array.from(modal.querySelectorAll(`${SIDE} ${THUMB} img`));
    images.forEach((image, index) => {
      image.loading = 'eager';
      image.decoding = 'async';
      try { image.fetchPriority = index < 6 ? 'high' : 'auto'; } catch (_) {}
      const src = clean(image.currentSrc || image.src);
      preloadImage(src, index < 6);
      image.decode?.().catch(() => {});
    });

    modal.querySelectorAll('.lcs-selected-size').forEach((size) => {
      const value = clean(size.textContent);
      if (value) {
        size.title = value;
        size.setAttribute('aria-label', `Size ${value}`);
      }
    });
  };

  const scheduleModalEnhancement = (modal) => {
    cancelAnimationFrame(frameId);
    frameId = requestAnimationFrame(() => eagerLoadSide(modal));
  };

  const closeAllExpanded = () => {
    pages().forEach((page) => {
      const controller = page.__lcsFavoritesPage;
      const modal = page.querySelector(MODAL);
      if (controller?.closeExpanded && (modal?.hidden === false || controller.activeExpandedItem)) {
        try { controller.closeExpanded(); } catch (_) {}
      }
      if (modal) {
        modal.hidden = true;
        modal.classList.remove('is-ready', 'has-expanded-options');
      }
      page.classList.remove('lcs-expanded-active');
      page.querySelectorAll('.lcs-favorite-expanded__menu').forEach((menu) => menu.classList.remove('has-expanded-options'));
    });

    document.querySelectorAll(MODAL).forEach((modal) => {
      modal.hidden = true;
      modal.classList.remove('is-ready', 'has-expanded-options');
    });
    document.querySelectorAll(OPENERS).forEach((button) => button.setAttribute('aria-expanded', 'false'));

    try { window.LCSExpandedModalLock?.unlock?.(); } catch (_) {}
    document.documentElement.classList.remove('lcs-expanded-solid-lock');
    document.body.classList.remove('lcs-expanded-solid-lock');
    document.documentElement.style.removeProperty('--lcs-modal-scroll-y');
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
  };

  const handleClose = (event) => {
    const target = event.target instanceof Element ? event.target.closest(CLOSE) : null;
    if (!target || !target.closest(MODAL)) return false;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    closeAllExpanded();
    return true;
  };

  const useThumbnailImmediately = (event) => {
    const thumb = event.target instanceof Element ? event.target.closest(`${MODAL}:not([hidden]) ${THUMB}`) : null;
    if (!thumb) return;
    const modal = thumb.closest(MODAL);
    const src = clean(thumb.dataset.expandedMediaSrc || thumb.querySelector('img')?.currentSrc || thumb.querySelector('img')?.src);
    const main = modal?.querySelector('[data-lcs-expanded-image]');
    if (src && main instanceof HTMLImageElement && main.src !== src) {
      preloadImage(src, true);
      main.src = src;
      main.hidden = false;
      main.removeAttribute('srcset');
      main.removeAttribute('sizes');
    }
    modal?.querySelectorAll(THUMB).forEach((item) => item.classList.toggle('is-active', item === thumb));
  };

  const bindModal = (modal) => {
    if (!(modal instanceof HTMLElement) || modal.dataset.sqFinalPolishBound === 'true') return;
    modal.dataset.sqFinalPolishBound = 'true';
    const observer = new MutationObserver(() => scheduleModalEnhancement(modal));
    observer.observe(modal, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'src'] });
    if (!modal.hidden) scheduleModalEnhancement(modal);
  };

  const initialize = (scope = document) => {
    if (scope instanceof Element && scope.matches(MODAL)) bindModal(scope);
    scope.querySelectorAll?.(MODAL).forEach(bindModal);
  };

  window.addEventListener('pointerup', (event) => {
    if (!handleClose(event)) return;
    suppressCloseClickUntil = Date.now() + 650;
  }, true);

  window.addEventListener('click', (event) => {
    const close = event.target instanceof Element ? event.target.closest(CLOSE) : null;
    if (!close) return;
    if (Date.now() < suppressCloseClickUntil) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return;
    }
    handleClose(event);
  }, true);

  window.addEventListener('pointerdown', (event) => {
    useThumbnailImmediately(event);
    const opener = event.target instanceof Element ? event.target.closest(OPENERS) : null;
    if (opener) void prefetchProduct(opener);
  }, true);

  window.addEventListener('pointerenter', (event) => {
    const opener = event.target instanceof Element ? event.target.closest(OPENERS) : null;
    if (opener) void prefetchProduct(opener);
  }, true);

  window.addEventListener('focusin', (event) => {
    const opener = event.target instanceof Element ? event.target.closest(OPENERS) : null;
    if (opener) void prefetchProduct(opener);
  }, true);

  document.addEventListener('change', (event) => {
    const modal = event.target instanceof Element ? event.target.closest(MODAL) : null;
    if (modal) scheduleModalEnhancement(modal);
  }, true);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  else initialize(document);
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
